import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { openIntercomMessage } from '~components/Intercom/intercom-utils';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { OmniReport } from '~hooks/useOmniReports/models';
import { useOmniReports } from '~hooks/useOmniReports/useOmniReports';
import useReportCategoryQuery from '~pages/Admin/ReportSettings/useReportCategoryQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

const REPORT_CATEGORY_HEIGHT_PX = 40;
const NESTED_REPORT_HEIGHT_PX = 32;
// Height of the fixed-position "Report an Issue" footer below; the
// VariableSizeList must reserve this much space so the last list item isn't
// hidden behind it when scrolled to the bottom.
const REPORT_ISSUE_FOOTER_HEIGHT_PX = 48;
export const REPORT_SETTINGS_SIDEBAR_WIDTH = 270;

const PRELOAD_BUFFER = 5;

interface ReportCategoryListProps {
  companyId: string;
  selectedContentId: string;
  onReportSelect: (contentId: string, reportId: string) => void;
}

const ReportCategoryList = ({
  companyId,
  selectedContentId,
  onReportSelect,
}: ReportCategoryListProps) => {
  const theme = useTheme();
  const reportCategoryQuery = useReportCategoryQuery(companyId);
  const listRef = useRef<VariableSizeList>(null);
  // Initialized to current companyId so the reset effect no-ops on mount —
  // otherwise a `?report=<id>` deep link would be wiped before it could render.
  const previousCompanyIdRef = useRef<string>(companyId);
  // Tracks the contentId we've already attempted to resolve. The deep-link
  // walk runs once per contentId; without this guard it would re-fire on
  // every render and refetch every category.
  const resolvedContentIdRef = useRef<string>('');

  // track the open/closed state of all report categories
  const [closedReportCategories, setClosedReportCategories] = useState<
    Record<string, boolean>
  >({});

  // Reports for each category, keyed by category id
  const [categoryReports, setCategoryReports] = useState<Record<string, OmniReport[]>>(
    {},
  );

  const { getOmniReportsByReportCategory } = useOmniReports();
  const { toasterStore } = useStores();

  // On real companyId change (not initial hydration from empty), reset state.
  useEffect(() => {
    if (companyId === previousCompanyIdRef.current) return;
    const wasEmpty = !previousCompanyIdRef.current;
    previousCompanyIdRef.current = companyId;

    if (wasEmpty) return;

    resolvedContentIdRef.current = '';
    setClosedReportCategories({});
    setCategoryReports({});
    onReportSelect('', '');
  }, [companyId]);

  // Resolve a deep-linked contentId. Fetches reports for any not-yet-loaded
  // category in parallel, finds the `OmniReport` whose `contentId` matches
  // the URL, then notifies the parent with the internal `report.id` so
  // `EmbeddedReport` can fetch the signed iframe URL. If no match, clears the
  // URL so the auto-select-first effect can take over.
  useEffect(() => {
    if (!selectedContentId) return;
    if (resolvedContentIdRef.current === selectedContentId) return;
    if (reportCategoryQuery.data.length === 0) return;

    resolvedContentIdRef.current = selectedContentId;

    const controller = new AbortController();
    const { signal } = controller;

    const resolve = async () => {
      const unfetchedCategories = reportCategoryQuery.data.filter(
        (category) => !(category.id in categoryReports),
      );

      let fetched: { categoryId: string; reports: OmniReport[] }[];
      try {
        fetched = await Promise.all(
          unfetchedCategories.map(async (category) => {
            const response = await getOmniReportsByReportCategory({
              companyId,
              reportCategoryId: category.id,
              signal,
            });

            return { categoryId: category.id, reports: response?.data ?? [] };
          }),
        );
      } catch (error) {
        if (signal.aborted) return;
        throw error;
      }

      if (signal.aborted) return;

      const allCategoryReports: Record<string, OmniReport[]> = { ...categoryReports };
      for (const { categoryId, reports } of fetched) {
        allCategoryReports[categoryId] = reports;
      }

      if (fetched.length > 0) {
        setCategoryReports(allCategoryReports);
      }

      for (const [categoryId, reports] of Object.entries(allCategoryReports)) {
        const match = reports.find((report) => report.contentId === selectedContentId);
        if (match) {
          setClosedReportCategories((prev) => ({ ...prev, [categoryId]: true }));
          onReportSelect(match.contentId, match.id);

          const categoryIndex = reportCategoryQuery.data.findIndex(
            (category) => category.id === categoryId,
          );
          if (categoryIndex !== -1 && listRef.current) {
            listRef.current.resetAfterIndex(categoryIndex, true);
          }
          return;
        }
      }

      // No category contains a report with this contentId. Notify the user
      // their link is stale, then clear so the auto-select-first effect below
      // can fall back to a valid report.
      toasterStore.push(
        alert(t('error_messages.omni.report_not_found'), AlertTypes.error),
      );
      onReportSelect('', '');
    };

    void resolve();

    return () => controller.abort();
  }, [reportCategoryQuery.data, selectedContentId, companyId]);

  // Auto-select first category's first report when no report is set in URL.
  useEffect(() => {
    if (selectedContentId) return;
    if (reportCategoryQuery.data.length === 0) return;

    const controller = new AbortController();
    const { signal } = controller;
    const firstCategory = reportCategoryQuery.data[0];

    setClosedReportCategories({ [firstCategory.id]: true });

    const selectFirstReport = async () => {
      try {
        const reports = await getOmniReportsByReportCategory({
          companyId,
          reportCategoryId: firstCategory.id,
          signal,
        });
        if (signal.aborted) return;

        setCategoryReports((prev) => ({
          ...prev,
          [firstCategory.id]: reports?.data ?? [],
        }));

        if (reports?.data && reports.data.length > 0) {
          const firstReport = reports.data[0];
          onReportSelect(firstReport.contentId, firstReport.id);

          if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
          }
        }
      } catch (error) {
        if (signal.aborted) return;
        throw error;
      }
    };

    void selectFirstReport();

    return () => controller.abort();
  }, [reportCategoryQuery.data, selectedContentId, companyId]);

  // Fetch reports when a category is expanded
  const handleReportCategoryClick = (categoryId: string) => {
    setClosedReportCategories((prev) => {
      const newState = {
        ...prev,
        [categoryId]: !prev[categoryId],
      };

      // If we're expanding and don't have reports yet, fetch them
      if (!prev[categoryId] && !categoryReports[categoryId]) {
        getOmniReportsByReportCategory({
          companyId,
          reportCategoryId: categoryId,
        }).then((reports) => {
          setCategoryReports((prev) => {
            const newReports = {
              ...prev,
              [categoryId]: reports?.data ?? [],
            };

            // Reset measurements after reports are loaded
            const categoryIndex = reportCategoryQuery.data.findIndex(
              (category) => category.id === categoryId,
            );
            if (categoryIndex !== -1 && listRef.current) {
              listRef.current.resetAfterIndex(categoryIndex, true);
            }

            return newReports;
          });
        });
      }

      // Reset list measurements for the toggle animation
      const categoryIndex = reportCategoryQuery.data.findIndex(
        (category) => category.id === categoryId,
      );
      if (categoryIndex !== -1 && listRef.current) {
        listRef.current.resetAfterIndex(categoryIndex, true);
      }

      return newState;
    });
  };

  const handleReportClick = (report: OmniReport) => {
    // Pre-mark this contentId as resolved so the deep-link effect doesn't
    // re-walk categories after the parent re-renders with the new URL.
    resolvedContentIdRef.current = report.contentId;
    onReportSelect(report.contentId, report.id);
  };

  // The report matching the selected contentId across all loaded categories,
  // used to pre-fill the reporting-request message with its name and id.
  const selectedReport =
    Object.values(categoryReports)
      .flat()
      .find((report) => report.contentId === selectedContentId) ?? null;

  // Opens the Intercom messenger pre-filled with the selected report's dashboard
  // link so support knows exactly which report the request is about.
  const handleReportIssueClick = () => {
    if (!selectedReport) return;

    openIntercomMessage(
      t('omni.reporting_request_message', {
        name: selectedReport.name,
        id: selectedReport.contentId,
        // Plain text for the Intercom messenger — keep punctuation in the
        // report name unescaped (i18next would otherwise turn ' into &#39;).
        interpolation: { escapeValue: false },
      }),
    );
  };

  const getItemSize = (index: number) => {
    const category = reportCategoryQuery.data[index];
    if (!category) return REPORT_CATEGORY_HEIGHT_PX;

    const isExpanded = closedReportCategories[category.id];
    const reportsCount = categoryReports[category.id]?.length || 0;

    const nestedReportsHeight = reportsCount * (NESTED_REPORT_HEIGHT_PX + 4);

    return isExpanded
      ? REPORT_CATEGORY_HEIGHT_PX + nestedReportsHeight
      : REPORT_CATEGORY_HEIGHT_PX;
  };

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      if (
        !reportCategoryQuery.loading &&
        reportCategoryQuery.hasMore &&
        visibleStopIndex >= reportCategoryQuery.data.length - PRELOAD_BUFFER
      ) {
        reportCategoryQuery.fetchMore();
      }
    },
    [reportCategoryQuery],
  );

  const ReportCategoryRow = ({ index, style }: ListChildComponentProps) => {
    const category = reportCategoryQuery.data[index];
    if (!category) return null;

    const reports = categoryReports[category.id] || [];
    const isExpanded = closedReportCategories[category.id];

    return (
      <Box key={category.id} style={style}>
        <ListItemButton
          onClick={() => handleReportCategoryClick(category.id)}
          sx={{
            py: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: theme.brandV2.colors.treadGray8,
            },
            '&.Mui-selected': {
              backgroundColor: theme.brandV2.colors.treadGray7,
            },
          }}
        >
          {isExpanded ? (
            <ExpandMore fontSize="small" />
          ) : (
            <KeyboardArrowRight fontSize="small" />
          )}
          <Box sx={{ flexGrow: 1, ml: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {category.name}
            </Typography>
          </Box>
        </ListItemButton>

        {/* Nested reports */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {reports.map((report) => (
              <ListItemButton
                key={report.id}
                sx={{
                  pl: 4,
                  mx: 2,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: theme.brandV2.colors.treadGray8,
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.brandV2.colors.treadGray7,
                    '&:hover': {
                      backgroundColor: theme.brandV2.colors.treadGray8,
                    },
                  },
                }}
                selected={selectedContentId === report.contentId}
                onClick={() => handleReportClick(report)}
              >
                <Typography variant="body2">{report.name}</Typography>
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </Box>
    );
  };

  if (reportCategoryQuery.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
        }}
      >
        <LoadingSpinner isVisible />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderLeft: 1,
        borderColor: 'divider',
        height: '100%',
        position: 'relative',
        width: REPORT_SETTINGS_SIDEBAR_WIDTH,
      }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <VariableSizeList
            ref={listRef}
            height={Math.max(0, height - REPORT_ISSUE_FOOTER_HEIGHT_PX)}
            itemCount={reportCategoryQuery.data.length}
            itemSize={getItemSize}
            width={width}
            onItemsRendered={handleItemsRendered}
            overscanCount={PRELOAD_BUFFER}
          >
            {ReportCategoryRow}
          </VariableSizeList>
        )}
      </AutoSizer>

      {reportCategoryQuery.loading && (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <LoadingSpinner isVisible />
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1,
        }}
      >
        <Tooltip title={t('omni.reporting_request_tooltip')}>
          {/* span wrapper lets the tooltip surface even when the button is
              disabled (no report selected) — disabled elements emit no events */}
          <Box component="span" sx={{ display: 'block' }}>
            <ListItemButton
              disabled={!selectedReport}
              onClick={handleReportIssueClick}
              sx={{
                width: '100%',
                py: 1.5,
                px: 2,
                backgroundColor: 'background.paper',
                borderTop: 1,
                borderColor: theme.brandV2.colors.treadGray8,
                color: theme.brandV2.colors.treadBlue,
                '&:hover': {
                  backgroundColor: theme.brandV2.colors.treadGray8,
                },
              }}
            >
              <ChatBubbleOutlineOutlined fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('omni.reporting_request')}
              </Typography>
            </ListItemButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ReportCategoryList;
